import React, { useEffect, useMemo, useRef, useState } from "react";
import { Wrapper } from "@googlemaps/react-wrapper";
import Marker from "./components/Marker";
import appConfig from "@appConfig";
import Drawers from "./components/Drawers";
import {
  getAllPinsData,
  pinsSelector,
  setCenter,
  setZoom,
} from "../../../../features/senninha/pinsSlice";
import { hubsSelector } from "../../../../features/senninha/hubsSlice";
import { oldSenninhaSelector } from "../../../../features/senninha/oldSenninhaSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  resetSelectedPreDeliveryPins,
  resetSelectedRelocatePins,
  routesManagementSelector,
  setRouteManagementOption,
} from "../../../../features/senninha/routesManagementSlice";
import { Dialog, Loading } from "@stonelog/stonelog-design-system";
import { getOptions } from "../../../../features/optionsSlice";
import { resetDeliverysState } from "../../../../features/senninha/trackingDeliveriesSlice";
import {
  getAlertUnproductivityJustificationTable,
  setResetAssessUnproductivityJustificationStatus,
  setResetJustificationStatus,
} from "../../../../features/senninha/GestaoEquipe/unproductivityJustificationSlice";
import OldSenninha from "../OldSenninha";
import Poligonos from "../OldSenninha/Poligono";
import CollectionHub from "../NewSenninha/CollectionHub";
import DeliveryPolygon from "../NewSenninha/Polygons/DeliveryPolygon";
import AreaSelector from "./components/AreaSelector";

import * as S from "./styles";

const Map = () => {
  // configs que referenciam o mapa
  const ref = useRef(null);
  const [map, setMap] = useState();
  const [isMarkerLibraryLoaded, setIsMarkerLibraryLoaded] = useState(false);
  const GMAPS_API_KEY = appConfig.mapsKey;

  const dispatch = useDispatch();

  const { center, zoom, pinsStatus, filteredPins, bouncedPin } = useSelector(
    pinsSelector
  );

  const [visibleBounds, setVisibleBounds] = useState(null);

  // Espelham o zoom/center atuais do Redux para o listener "idle" (registrado
  // uma única vez) comparar contra o valor mais recente sem re-registrar.
  const zoomRef = useRef(zoom);
  const centerRef = useRef(center);
  zoomRef.current = zoom;
  centerRef.current = center;

  const { selectedHubInfos, selectedDistrictInfos } = useSelector(hubsSelector);

  const { oldSenninhaWorkFlow } = useSelector(oldSenninhaSelector);

  const { areaSelectionMode } = useSelector(routesManagementSelector);
  const mapWorkflow = useSelector((state) => state.pinsSlice.mapWorkflow);

  useEffect(() => {
    dispatch(
      getAllPinsData({
        provider: selectedHubInfos?.children, // children é o valor do hub/polo
      })
    );
    dispatch(resetDeliverysState());
    dispatch(getOptions());
    dispatch(resetSelectedRelocatePins());
    dispatch(resetSelectedPreDeliveryPins());
    dispatch(setRouteManagementOption("realocar"));
  }, []);

  useEffect(() => {
    dispatch(
      getAlertUnproductivityJustificationTable({
        polo: selectedHubInfos?.children,
        distrito: selectedDistrictInfos?.children,
      })
    );
    dispatch(setResetJustificationStatus());
    dispatch(setResetAssessUnproductivityJustificationStatus());
  }, []);

  useEffect(() => {
    window.google.maps
      .importLibrary("marker")
      .then(() => setIsMarkerLibraryLoaded(true));
  }, []);

  useEffect(() => {
    if (ref.current !== null && map === undefined && isMarkerLibraryLoaded) {
      setMap(
        new window.google.maps.Map(ref.current, {
          mapId: `${Math.random()}`,
          clickableIcons: false,
          disableDefaultUI: true,
          center: { lat: -22.8996166, lng: -43.1978316 },
          zoom: 12,
          zoomControl: true,
        })
      );
    }
    if (map !== undefined) {
      map.setCenter(center);
      map.setZoom(zoom);
    }
  }, [map, isMarkerLibraryLoaded, center, zoom]);

  useEffect(() => {
    if (!map) return;
    let lastKey = "";
    const listener = map.addListener("idle", () => {
      const b = map.getBounds();
      if (!b) return;
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      const key = `${ne.lat()},${ne.lng()},${sw.lat()},${sw.lng()}`;
      if (key !== lastKey) {
        lastKey = key;
        setVisibleBounds(b);
      }
      // Sincroniza o gesto do usuário (zoom por scroll/botões, pan) de volta
      // para o Redux. Sem isso, o useEffect de reconciliação acima reaplica o
      // zoom/center antigos e "reverte" o gesto — o que dava a sensação de que
      // o mapa não permitia zoom durante o desenho de área (laço/quadrado).
      const currentZoom = map.getZoom();
      if (currentZoom != null && currentZoom !== zoomRef.current) {
        dispatch(setZoom(currentZoom));
      }
      const c = map.getCenter();
      if (c) {
        const nextCenter = { lat: c.lat(), lng: c.lng() };
        const prev = centerRef.current;
        if (
          !prev ||
          prev.lat !== nextCenter.lat ||
          prev.lng !== nextCenter.lng
        ) {
          dispatch(setCenter({ center: nextCenter }));
        }
      }
    });
    return () => listener.remove();
  }, [map, dispatch]);

  const visiblePins = useMemo(() => {
    if (!filteredPins?.length) return [];
    if (!visibleBounds) return filteredPins;
    return filteredPins.filter(
      (pin) =>
        visibleBounds.contains({ lat: pin.lat, lng: pin.lng }) ||
        pin.id === bouncedPin
    );
  }, [filteredPins, visibleBounds, bouncedPin]);

  return (
    <>
      {pinsStatus === "pending" && (
        <S.ScreenMask>
          <Dialog.Root closable={false} open={true}>
            <Dialog.Header />
            <Dialog.ContentHeader
              ilustration={<Loading />}
              description={
                <S.TextContent>
                  Segura aí! Estamos carregando os pins para que você possa
                  utilizar o Senninha.
                </S.TextContent>
              }
            />
            <Dialog.Footer />
          </Dialog.Root>
        </S.ScreenMask>
      )}
      <Wrapper
        apiKey={GMAPS_API_KEY}
        libraries={["drawing", "geometry"]}
        version="3.64"
      >
        <Drawers />
        <OldSenninha />

        <S.MapContainer ref={ref}>
          {map && (
            <>
              <Poligonos map={map} />
              <DeliveryPolygon map={map} />
              <CollectionHub map={map} />
              {areaSelectionMode && mapWorkflow?.includes("routesDrawer") && (
                <AreaSelector map={map} mode={areaSelectionMode} />
              )}
            </>
          )}
          {map &&
          pinsStatus !== "pending" &&
          isMarkerLibraryLoaded &&
          visiblePins.length &&
          oldSenninhaWorkFlow !== "poligonos" ? (
            visiblePins.map((dataPin) => {
              return (
                <Marker
                  key={dataPin.id ?? `${dataPin.lat},${dataPin.lng}`}
                  idx={dataPin.id}
                  map={map}
                  position={{ lat: dataPin.lat, lng: dataPin.lng }}
                  dataPin={dataPin}
                />
              );
            })
          ) : (
            <></>
          )}
        </S.MapContainer>
      </Wrapper>
    </>
  );
};

export default Map;
